/**
 * Persistence layer — single source of truth for the at-rest scene shape.
 *
 * Every persisted document (.pr file, IndexedDB cache, session) flows through
 * one of the document types defined here. Encoding and decoding live in
 * `codec.ts` and `sceneDocument.ts`; this file contains types only.
 *
 * Design rules:
 *  - Every field has a definite type. Optional values are `T | null`, never
 *    `T | undefined`. Maps that can be empty are `{}`. Arrays that can be
 *    empty are `[]`. This forces every encode/decode path to handle every
 *    field explicitly — no silent drops.
 *  - The serialized SceneObject mirrors the live `SceneObject` 1:1 with
 *    nested topology, so encode/decode is a flat structural walk.
 *  - `imported` objects are filtered before encode and never appear.
 */

import type {
  CurveData,
  MaterialDataBlock,
  ObjectKind,
  PivotReference,
  SceneLightProps,
  SceneMaterial,
  ShadingMode,
} from "../../modeling/types.ts";
import type {
  CameraBookmark,
  ModifierOverride,
  ProportionalFalloff,
  SavedInkingView,
  SnapTarget,
} from "../storeTypes.ts";

// ---------------------------------------------------------------------------
// Geometry — base64-encoded typed arrays
// ---------------------------------------------------------------------------

export interface SerializedTopology {
  /** base64 Uint32Array — polygon face offsets */
  offsets: string;
  /** base64 Uint32Array — polygon face corners */
  corners: string;
  /** base64 Uint32Array — edge pairs [v0, v1, ...] */
  edges: string;
  /** base64 Uint32Array — per-corner edge index */
  cornerEdges: string;
  /** base64 Uint8Array — per-edge bitfield flags */
  edgeFlags: string;
  /** base64 Float32Array — per-edge subsurf crease weight in [0, 1]. Omitted
   *  (null on disk, undefined at runtime) when every weight is 0. Length,
   *  when present, is `edges.length / 2`. */
  edgeCreases?: string | null;
}

/** Serialized form of `VertexGroup`. Indices are stored as a sorted plain
 *  array for compact, deterministic JSON output. */
export interface SerializedVertexGroup {
  id: string;
  name: string;
  /** Shared-vertex indices, sorted ascending. */
  indices: number[];
}

/** Serialized form of `MaterialSlot`. */
export interface SerializedMaterialSlot {
  id: string;
  materialId: string;
}

/** Serialized form of `FaceGroup`. Indices are stored sorted ascending for
 *  compact, deterministic JSON output. */
export interface SerializedFaceGroup {
  id: string;
  name: string;
  /** Face indices, sorted ascending. */
  faces: number[];
}

/**
 * Serialized form of `FaceAttributeTable`. Each typed-array field is
 * base64-encoded; `null` when omitted. `groups` is `null` when empty.
 */
export interface SerializedFaceAttributeTable {
  /** base64 Uint16Array — per-face material slot index (null when every face uses slot 0). */
  materialIndex: string | null;
  /** base64 Uint8Array — per-face shading override (null when every face inherits). */
  shading: string | null;
  /** Named face groups; `null` when the list is empty. */
  groups: SerializedFaceGroup[] | null;
}

// ---------------------------------------------------------------------------
// Scene-level sketch — projected (surface-locked) and screenspace strokes.
// ---------------------------------------------------------------------------
//
// Strokes live on the scene, not on individual SceneObjects. Each stroke
// declares its `kind` so projected and screenspace strokes can interleave
// inside a single layer in render order.

export interface SerializedProjectedSketchPoint {
  attachment: { objectId: string; topologyVersion: number };
  /** Mesh-local bind-pose position. */
  p: [number, number, number];
  pressure: number;
  skinIndex?: [number, number, number, number];
  skinWeight?: [number, number, number, number];
  corners?: [number, number, number];
  bary?: [number, number, number];
}

export interface SerializedScreenspaceSketchPoint {
  /** World-space position, baked at draw time. */
  p: [number, number, number];
  pressure: number;
}

export interface SerializedProjectedSketchStroke {
  kind: "projected";
  id: string;
  tool: "pencil" | "erase";
  color: string;
  thickness: number;
  worldThickness?: number;
  opacity: number;
  fallbackPlane?: {
    point: [number, number, number];
    normal: [number, number, number];
  };
  points: SerializedProjectedSketchPoint[];
}

export interface SerializedScreenspaceSketchStroke {
  kind: "screenspace";
  id: string;
  tool: "pencil" | "erase";
  color: string;
  thickness: number;
  worldThickness?: number;
  opacity: number;
  points: SerializedScreenspaceSketchPoint[];
}

export type SerializedSketchStroke =
  | SerializedProjectedSketchStroke
  | SerializedScreenspaceSketchStroke;

export interface SerializedSketchLayer {
  id: string;
  name: string;
  visible: boolean;
  opacity: number;
  strokes: SerializedSketchStroke[];
}

export interface SerializedSceneSketchState {
  layers: SerializedSketchLayer[];
}

/**
 * JSON-friendly representation of a `SceneObject`.
 *
 * Geometry is encoded as a unit: when `topology` is null, all of
 * `vertices` / `indices` / `loopNormals` / `uvs` / `baseVertices`
 * are also null. This branch exists for unsculpted
 * mannequins, whose mesh is reconstructed from the GLB at load time.
 * Every other object always has a complete geometry block.
 */
export interface SerializedSceneObject {
  id: string;
  name: string;
  objectKind: ObjectKind;
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];

  // Geometry block — present together, or all null for omitted-geo mannequins.
  /** base64 Float32Array */
  vertices: string | null;
  /** base64 Uint32Array */
  indices: string | null;
  /** base64 Float32Array — per-loop (per face-corner) normals */
  loopNormals: string | null;
  topology: SerializedTopology | null;
  /** base64 Float32Array — per-corner UVs */
  uvs: string | null;
  /** base64 Float32Array — pre-sculpt reference vertices */
  baseVertices: string | null;
  /** base64 Float32Array — per-vertex skin radius scale factors (null when all 1.0) */
  skinRadii: string | null;

  /**
   * Named vertex groups (shared-vertex indices). `null` when the list is
   * empty — matches the in-memory "omit when empty" invariant and keeps old
   * `.pr` files without this field decoding as `undefined` (no migration).
   */
  vertexGroups: SerializedVertexGroup[] | null;

  /** Material slots (Blender-style list). Always ≥1 entry. */
  materialSlots: SerializedMaterialSlot[] | null;
  shading: ShadingMode;
  /** Auto-smooth threshold in degrees, or null when default. */
  autoSmoothAngle: number | null;
  /**
   * Per-face attribute table — material assignment, shading overrides, and
   * named face groups. `null` when none of its fields would carry meaningful
   * data, mirroring the in-memory "omit when empty" invariant.
   */
  faceAttributes: SerializedFaceAttributeTable | null;

  // Object-kind-specific
  light: SceneLightProps | null;
  mannequinModelId: string | null;
  /** Local-space origin offset for mannequins; [0,0,0] when unset. */
  originOffset: [number, number, number];
  curveData: CurveData | null;
  /**
   * Image plane reference — content hash + intrinsic pixel dimensions. The
   * blob URL is reconstituted on load via `getReferenceImageUrl(imageKey)`,
   * matching the reference-layer pattern. `null` for non-image-plane objects.
   */
  imagePlane: {
    imageKey: string;
    intrinsicWidth: number;
    intrinsicHeight: number;
  } | null;

  /**
   * Optional generator reference — see `modeling/generators/types.ts` for
   * the full invariant. Omitted (null) for fully baked / hand-authored
   * objects, which is the overwhelming common case.
   */
  generator?: SerializedGeneratorRef | null;
}

/**
 * Serialized form of a single-mesh `GeneratorRef` stored on a SceneObject.
 * Compound (collection-owned) generator refs are serialized separately
 * on the collection — see `SerializedCollection.generatorRef`.
 *
 * `params` is a recursive record: a leaf value is a number / boolean /
 * string, and a list-typed param serializes as an array of item records
 * whose own `params` follow the same shape. The codec reconciles this
 * against the generator's live spec tree on load so schema growth never
 * breaks old saves.
 */
export interface SerializedGeneratorRef {
  generatorId: string;
  params: Record<string, unknown>;
  /**
   * Identity token over the inputs that affected produced geometry at the
   * last rebuild — see `GeneratorRef.geometrySignature`. Persisted so a
   * draped/mutated generator object survives save/reload without the first
   * post-load style edit resetting vertex positions. Omitted when the
   * generator didn't declare a signature hook. Opaque to the codec: the
   * value is round-tripped verbatim and only the runtime side knows how to
   * reproduce it.
   */
  geometrySignature?: string;
}

/**
 * Serialized form of a collection-owned compound `GeneratorRef`. Shares
 * `params` shape with `SerializedGeneratorRef` and additionally persists
 * the stable `roleToObjectId` map so rebuild-in-place can resume across
 * save/load.
 */
export interface SerializedCollectionGeneratorRef {
  generatorId: string;
  params: Record<string, unknown>;
  roleToObjectId: Record<string, string>;
}

/**
 * Serialized form of a boolean cutter collection ref. `objectIds` remains
 * the complete collection membership; this ref identifies the boolean host
 * and the member subset used as cutters.
 */
export interface SerializedCollectionCutterRef {
  hostObjectId: string;
  cutterObjectIds: string[];
}

// ---------------------------------------------------------------------------
// Material data block — serialized form
// ---------------------------------------------------------------------------

export interface SerializedMaterialDataBlock {
  id: string;
  name: string;
  /** Null when identical to DEFAULT_MATERIAL (save space). */
  properties: SceneMaterial | null;
}

// ---------------------------------------------------------------------------
// Top-level scene state body — shared by SceneDocument and SessionDocument
// ---------------------------------------------------------------------------

export interface SerializedBlendFileRef {
  id: string;
  fileName: string;
  sha256: string;
  objectNames: string[];
}

export interface SerializedGroupTransform {
  position: [number, number, number];
  rotation: [number, number, number, number];
  scale: [number, number, number];
}

export interface SerializedReferenceLayer {
  id: string;
  name: string;
  imageKey: string;
  offset: [number, number];
  scale: number;
  rotation: number;
  opacity: number;
  above: boolean;
  visible: boolean;
  vanishingLines: Array<{
    id: string;
    axis: "x" | "y" | "z";
    start: [number, number];
    end: [number, number];
  }>;
  groundPlaneZ: number;
  liveSolve: boolean;
  referenceDistance?: {
    p1: [number, number];
    p2: [number, number];
    length: number;
  };
}

/**
 * Serialized form of `RenderConfig` (see `app/renderConfig.ts`). One instance
 * per scope (viewport / render). Every field is required on the wire — nullable
 * types would force every decode path to handle missing fields, which is a
 * footgun.
 */
export interface SerializedRenderConfig {
  viewMode: "matcap" | "material" | "wireframe";
  showWireframe: boolean;
  wireframeXray: boolean;
  matcapPreset: string;
  ambientColor: string;
  ambientIntensity: number;
  aoEnabled: boolean;
  aoIntensity: number;
  bloomEnabled: boolean;
  bloomIntensity: number;
  bloomThreshold: number;
  shadowsEnabled: boolean;
  shadowSoftness: number;
  envMapEnabled: boolean;
  envMapPreset: string;
  envMapIntensity: number;
  envMapBackground: boolean;
  viewportBgPreset: string;
}

/** Serialized form of `ViewportQualitySettings`. */
export interface SerializedViewportQuality {
  maxViewportDpr: number;
  shadowMapResolution: number;
}

/**
 * Serialized form of `ExportQualityOverrides`. `null` = inherit viewport.
 */
export interface SerializedExportQualityOverrides {
  maxViewportDpr: number | null;
  shadowMapResolution: number | null;
}

export interface SerializedCollection {
  id: string;
  name: string;
  transform: SerializedGroupTransform;
  objectIds: string[];
  /**
   * Optional cutter-group metadata — present when this collection owns
   * hidden boolean cutter objects for a host object.
   */
  cutterRef?: SerializedCollectionCutterRef | null;
  /**
   * Optional compound generator reference — present when this collection
   * is the authoritative owner of a generator instance (e.g. a Room).
   * Omitted (null) on hand-collected groups.
   */
  generatorRef?: SerializedCollectionGeneratorRef | null;
}

/**
 * The full scene state body. Every persisted field of `AppState` lives here.
 * Wrapped in `SceneDocument` (for `.pr` files / scene cache) or
 * `SessionDocument` (for session restore) — both formats share this body.
 */
export interface SerializedSceneBody {
  // Identity / timestamps
  createdAt: number;
  modifiedAt: number;

  // Blend files & group transforms
  blendFileRefs: SerializedBlendFileRef[];
  groupTransforms: Record<string, SerializedGroupTransform>;

  // Scene objects (canonical)
  sceneObjects: SerializedSceneObject[];

  // Material registry
  materials: SerializedMaterialDataBlock[];

  // Per-object state maps
  shapeKeyWeights: Record<string, Record<string, number>>;
  visibility: Record<string, boolean>;
  renderVisibility: Record<string, boolean>;
  shadingOverrides: Record<string, ShadingMode>;
  modifierOverrides: Record<string, ModifierOverride[]>;
  favoriteShapeKeys: string[];

  // Camera
  focalLength: number;
  cameraBookmarks: CameraBookmark[];
  cameraPosition: [number, number, number] | null;
  cameraTarget: [number, number, number] | null;

  // Inking — persisted full-resolution raster snapshots + their camera state.
  // `savedInkingViews` is optional so older scene files (which predate this
  // feature) still load cleanly without migration.
  savedInkingViews?: SavedInkingView[];

  // Reference image layers
  referenceLayers: SerializedReferenceLayer[];
  activeReferenceLayerId: string | null;

  // Per-scope render method + its settings (viewMode, matcap, AO, bloom,
  // shadows, env map, path-trace, etc.). Independent for the creation and
  // output workspaces. See `app/renderConfig.ts` for the `RenderConfig`
  // shape. Stored as a plain record so the document stays JSON-friendly.
  renderConfigs: {
    viewport: SerializedRenderConfig;
    render: SerializedRenderConfig;
  };

  // Live-canvas quality — shared by every workspace. Export-time
  // overrides push offscreen renders higher without affecting the
  // interactive canvas.
  viewportQuality: SerializedViewportQuality;
  exportQualityOverrides: SerializedExportQualityOverrides;

  // Viewport — global (non-scoped) options
  showTextures: boolean;
  textureMapsEnabled: boolean;
  objectXray: boolean;
  showGrid: boolean;

  // Render output (pixel-based — see storeTypes.ts renderWidth/renderHeight)
  renderWidth: number;
  renderHeight: number;

  // Mannequin state
  mannequinMorphs: Record<string, Record<string, number>>;
  mannequinBonePoses: Record<
    string,
    Record<string, [number, number, number, number]>
  >;
  pinnedBones: Record<string, Record<string, [number, number, number]>>;
  boneVolumes: Record<string, Record<string, number>>;
  // Editing
  pivotReference: PivotReference;
  proportionalEnabled: boolean;
  proportionalRadius: number;
  proportionalFalloff: ProportionalFalloff;
  snapEnabled: boolean;
  snapTarget: SnapTarget;
  snapWithRotation: boolean;
  surfaceProjectIndividual: boolean;

  // Collections
  collections: SerializedCollection[];

  // 3D cursor — position round-trips; selection is transient.
  cursor3D?: {
    position: [number, number, number];
  };

  /** Scene-level sketch document — projected and screenspace strokes. */
  sketch: SerializedSceneSketchState;
}

// ---------------------------------------------------------------------------
// Document envelopes — single magic, version, and kind discriminator
// ---------------------------------------------------------------------------

/** Magic bytes shared by every persistence document. */
export const POSEREF_MAGIC = "POSEREF" as const;
/**
 * Schema version. Bumping this breaks every existing document — the
 * persistence worker rejects documents whose version does not match.
 *
 * v2: `Collection` is a first-class transform parent. Every
 * `SerializedCollection` carries a required `transform: SerializedGroupTransform`
 * field, and scene-object members are stored in collection-local space.
 * v3: Sculpt persistence is reduced to direct base-mesh deformation.
 * Sculpt layers, masks, and multi-resolution state are removed from the
 * schema entirely.
 * v4: Scene strokes are decoupled from individual SceneObjects. Strokes and
 * layers live at the scene level, with projected and screenspace stroke
 * kinds. The per-object `surfaceStrokes` field is removed.
 * v5: Sketch is the only stroke workspace. Projected stroke points carry
 * their own attachments so one stroke can cross multiple surfaces. No
 * migration from older versions - legacy documents are rejected at the
 * version gate.
 */
export const POSEREF_VERSION = 5 as const;

interface BaseDocument {
  magic: typeof POSEREF_MAGIC;
  version: typeof POSEREF_VERSION;
}

/** A scene saved as a `.pr` file or stored in the IndexedDB scene cache. */
export interface SceneDocument extends BaseDocument, SerializedSceneBody {
  kind: "scene";
}

/**
 * Session restore document. Inlines every scene field plus session-only
 * metadata so the page-refresh path doesn't need a nested re-parse.
 *
 * `libraryItemId` identifies the cloud scene this session is a backup of.
 * On open-from-library, we compare the session's `modifiedAt` against the
 * cloud item's `updatedAt` to decide whether to seamlessly load the local
 * cache (newer) or discard it (stale). Null when the session is a blank
 * scene that has never been opened or saved to the library.
 */
export interface SessionDocument extends BaseDocument, SerializedSceneBody {
  kind: "session";
  blendFileName: string | null;
  blendFileHash: string | null;
  currentSceneFileName: string | null;
  sceneDirty: boolean;
  libraryItemId: string | null;
}

/** A single library object. Pure geometry — no app state. */
export interface LibraryObjectDocument extends BaseDocument {
  kind: "object";
  createdAt: number;
  modifiedAt: number;
  object: SerializedSceneObject;
}

/**
 * A library collection — multiple objects with positions stored relative to
 * the group's centroid at save time, so the group can be re-spawned anywhere.
 */
export interface LibraryCollectionDocument extends BaseDocument {
  kind: "collection";
  createdAt: number;
  modifiedAt: number;
  centroid: [number, number, number];
  objects: SerializedSceneObject[];
}

/** Discriminated union of every persistence document type. */
export type PoseRefDocument =
  | SceneDocument
  | SessionDocument
  | LibraryObjectDocument
  | LibraryCollectionDocument;

// ---------------------------------------------------------------------------
// Worker boundary types (Layer 1) — main thread ↔ persistenceWorker
// ---------------------------------------------------------------------------
//
// The main thread captures a "raw" document where `sceneObjects` is still a
// list of live `SceneObject` references (typed arrays intact). Structured
// clone carries it across the worker boundary; the worker runs each object
// through `encodeSceneObject` from `codec.ts` and then JSON.stringifies the
// result. The inverse path produces a raw document on load.
//
// These types are *not* at-rest formats — they only exist between the main
// thread and the worker. They have the same shape as `SceneDocument` /
// `SessionDocument` except `sceneObjects` is the live form.

import type { SceneObject } from "../../modeling/types.ts";

type RawBodyOverrides = {
  /** Live `SceneObject` references — typed arrays carried via structured clone. */
  sceneObjects: SceneObject[];
  /** Live material data blocks — carried via structured clone. */
  materials: MaterialDataBlock[];
};

export type RawSceneDocument = Omit<SceneDocument, keyof RawBodyOverrides> &
  RawBodyOverrides;

export type RawSessionDocument = Omit<SessionDocument, keyof RawBodyOverrides> &
  RawBodyOverrides;
